import axiosNew from '../../../helpers/axios'
import { getClientTimezone } from './workload.utils'

const parseFilenameFromDisposition = (disposition, fallback) => {
    const matched = String(disposition || '').match(
        /filename\*?=(?:UTF-8''|")?([^";]+)/i
    )
    if (!matched?.[1]) return fallback
    try {
        return decodeURIComponent(matched[1].replace(/"/g, ''))
    } catch {
        return matched[1].replace(/"/g, '') || fallback
    }
}

const triggerBlobDownload = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
}

const readBlobErrorMessage = async (error, fallback) => {
    const data = error?.response?.data
    if (data instanceof Blob) {
        try {
            const text = await data.text()
            const json = JSON.parse(text)
            return json?.message || fallback
        } catch {
            return fallback
        }
    }
    return data?.message || error?.message || fallback
}

export const fetchProjectTimelogWorkload = async (payload = {}) => {
    const {
        start,
        end,
        projectId = [],
        userId,
        timezone = getClientTimezone(),
    } = payload

    const data = { start, end, userId, timezone }
    if (Array.isArray(projectId) && projectId.length > 0) {
        data.projectId = projectId
    }

    return axiosNew({
        url: 'support/reports/workload/project-timelog',
        method: 'POST',
        data,
    })
}

export const fetchUserTimelogWorkload = async (payload = {}) => {
    const {
        skip = 0,
        limit = 10,
        search = '',
        start,
        end,
        projectId = [],
        userId = [],
        timezone = getClientTimezone(),
    } = payload

    const data = {
        skip,
        limit,
        search,
        start,
        end,
        userId,
        timezone,
    }
    if (Array.isArray(projectId) && projectId.length > 0) {
        data.projectId = projectId
    }

    return axiosNew({
        url: 'support/reports/workload/user-timelog-list',
        method: 'POST',
        data,
    })
}

export const fetchWorkloadScreenshotsList = async (payload = {}) => {
    const {
        skip = 0,
        limit = 20,
        start,
        end,
        projectId = [],
        userId,
        assigneeId = [],
    } = payload

    const data = {
        skip,
        limit,
        start: start != null ? String(start) : undefined,
        end: end != null ? String(end) : undefined,
    }

    if (userId) data.userId = userId
    if (Array.isArray(projectId) && projectId.length > 0) {
        data.projectId = projectId
    }
    if (Array.isArray(assigneeId) && assigneeId.length > 0) {
        data.assigneeId = assigneeId
    }

    return axiosNew({
        url: 'support/reports/screenshots/list',
        method: 'POST',
        data,
    })
}

/** Download live workload PDF (nothing persisted). */
export const downloadWorkloadPdf = async (payload = {}) => {
    try {
        const resp = await axiosNew({
            url: 'support/reports/workload/pdf',
            method: 'POST',
            data: payload,
            responseType: 'blob',
        })

        const contentType = resp?.headers?.['content-type'] || ''
        if (contentType.includes('application/json')) {
            const text = await resp.data.text()
            const json = JSON.parse(text)
            return {
                success: false,
                message: json?.message || 'Failed to download PDF',
            }
        }

        const fileName = parseFilenameFromDisposition(
            resp?.headers?.['content-disposition'],
            'Workload report.pdf'
        )
        triggerBlobDownload(
            new Blob([resp.data], { type: 'application/pdf' }),
            fileName
        )
        return { success: true, fileName }
    } catch (error) {
        return {
            success: false,
            message: await readBlobErrorMessage(
                error,
                'Failed to download PDF'
            ),
        }
    }
}

/** Persist a saved report from filters (server recomputes snapshot). */
export const saveWorkloadReport = async (payload = {}) => {
    try {
        const resp = await axiosNew({
            url: 'support/reports/saved',
            method: 'POST',
            data: payload,
        })
        return {
            success: Boolean(resp?.data?.success ?? true),
            message: resp?.data?.message || 'Report saved',
            data: resp?.data?.data ?? resp?.data ?? null,
        }
    } catch (error) {
        return {
            success: false,
            message:
                error?.response?.data?.message ||
                error?.message ||
                'Failed to save report',
            data: null,
        }
    }
}

export const listSavedWorkloadReports = async (payload = {}) => {
    const {
        skip = 0,
        limit = 20,
        search = '',
        projectId = null,
        createdBy = null,
    } = payload

    return axiosNew({
        url: 'support/reports/saved/list',
        method: 'POST',
        data: { skip, limit, search, projectId, createdBy },
    })
}

export const getSavedWorkloadReport = async (id) =>
    axiosNew({
        url: `support/reports/saved/${id}`,
        method: 'GET',
    })

export const updateSavedWorkloadReport = async (id, payload = {}) => {
    try {
        const resp = await axiosNew({
            url: `support/reports/saved/${id}`,
            method: 'PATCH',
            data: payload,
        })
        return {
            success: Boolean(resp?.data?.success ?? true),
            message: resp?.data?.message || 'Report updated',
            data: resp?.data?.data ?? resp?.data ?? null,
        }
    } catch (error) {
        return {
            success: false,
            message:
                error?.response?.data?.message ||
                error?.message ||
                'Failed to update report',
            data: null,
        }
    }
}

export const deleteSavedWorkloadReport = async (id) => {
    try {
        const resp = await axiosNew({
            url: `support/reports/saved/${id}`,
            method: 'DELETE',
        })
        return {
            success: Boolean(resp?.data?.success ?? true),
            message: resp?.data?.message || 'Report deleted',
        }
    } catch (error) {
        return {
            success: false,
            message:
                error?.response?.data?.message ||
                error?.message ||
                'Failed to delete report',
        }
    }
}

export const downloadSavedWorkloadPdf = async (id) => {
    try {
        const resp = await axiosNew({
            url: `support/reports/saved/${id}/pdf`,
            method: 'GET',
            responseType: 'blob',
        })

        const contentType = resp?.headers?.['content-type'] || ''
        if (contentType.includes('application/json')) {
            const text = await resp.data.text()
            const json = JSON.parse(text)
            return {
                success: false,
                message: json?.message || 'Failed to download PDF',
            }
        }

        const fileName = parseFilenameFromDisposition(
            resp?.headers?.['content-disposition'],
            'report.pdf'
        )
        triggerBlobDownload(
            new Blob([resp.data], { type: 'application/pdf' }),
            fileName
        )
        return { success: true, fileName }
    } catch (error) {
        return {
            success: false,
            message: await readBlobErrorMessage(
                error,
                'Failed to download PDF'
            ),
        }
    }
}
